// RECALL-ROLES-ONBOARDING: the people side of memory, kept simple for people. Three small cards at
// the top of the Inbox ("Is this you?" for the profile drafted from their seat, "Take over this
// seat's memory", the Chief of Staff's onboarding), a charter modal for a space and the company,
// and the admin's roles panel. Until the memory service takes the acts they are kept here and the
// words "Waiting on memory service." show; nothing errors. What each thing is made of sits behind (i).
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InfoTip,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../settings/problem-text";
import { featureState, useSession } from "../use-feature";
import { memoryKeys } from "./memory-queries";

const peopleKey = (co: string, ...rest: string[]) =>
  [...memoryKeys.all(co), "people", ...rest] as const;

function usePeopleEnabled(company: CompanyAccess): boolean {
  const { me } = useSession();
  return (
    featureState(me, company.id, "memory").enabled &&
    me.memberships.some((m) => m.company_id === company.id && m.kind === "person")
  );
}

/** "Waiting on memory service." when the memory service does not take the acts yet. */
export function WaitingNote({ label }: { label: string | null | undefined }) {
  if (!label) return null;
  return (
    <span data-slot="memory-waiting" className="text-xs text-muted-foreground">
      {label}
    </span>
  );
}

function Card({
  slot,
  title,
  info,
  children,
}: {
  slot: string;
  title: string;
  info: string;
  children: ReactNode;
}) {
  return (
    <section
      data-slot={slot}
      aria-label={title}
      className="mx-4 mt-3 shrink-0 rounded-md border border-subtle bg-surface px-3 py-2.5"
    >
      <header className="flex items-center gap-1">
        <h2 className="text-sm font-semibold">{title}</h2>
        <InfoTip label={`About ${title}`}>{info}</InfoTip>
      </header>
      {children}
    </section>
  );
}

/** The three Inbox cards, in the order a person meets them. */
export function PeopleCards({ api, company }: { api: Api; company: CompanyAccess }) {
  const enabled = usePeopleEnabled(company);
  if (!enabled) return null;
  return (
    <>
      <OnboardingCard api={api} company={company} />
      <ProfileCard key={company.id} api={api} company={company} />
      <SuccessionCards api={api} company={company} />
    </>
  );
}

function ProfileCard({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const qc = useQueryClient();
  const me = useSession().me.memberships.find((m) => m.company_id === co && m.kind === "person");
  const meId = me?.membership_id ?? "";
  const key = peopleKey(co, "card");
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.client.memoryPeople.card({ params: { co, id: meId } }),
    enabled: !!me,
    retry: false,
  });
  const [text, setText] = useState<string | null>(null);
  const settle = useMutation({
    mutationFn: (accept: boolean) =>
      accept
        ? api.client.memoryPeople.profileAccept({
            params: { co },
            body: text === null ? {} : { text },
          })
        : api.client.memoryPeople.profileDiscard({ params: { co } }),
    onSuccess: (next) => qc.setQueryData(key, next),
    onError: (e) =>
      toast.add({
        title: "Could not save",
        description: problemText(e, "Try again."),
        type: "error",
      }),
  });
  const draft = q.data?.profile;
  if (!draft) return null;
  return (
    <Card
      slot="profile-card"
      title="Is this you?"
      info="Drafted from your seat: your role, what you are accountable for and what to ask you about. Change it or say no; nothing is shared until you say yes."
    >
      <p className="mt-1 text-[13px] font-medium">{draft.role}</p>
      <Textarea
        aria-label="About you"
        className="mt-1 text-[13px]"
        rows={2}
        value={text ?? draft.text}
        onChange={(e) => setText(e.target.value)}
      />
      {draft.skills.length ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Ask you about: {draft.skills.join(", ")}
        </p>
      ) : null}
      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" disabled={settle.isPending} onClick={() => settle.mutate(true)}>
          Yes, this is me
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={settle.isPending}
          onClick={() => settle.mutate(false)}
        >
          No
        </Button>
        <WaitingNote label={q.data?.doors.label} />
      </div>
    </Card>
  );
}

function SuccessionCards({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const qc = useQueryClient();
  const key = peopleKey(co, "successions");
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.client.memoryPeople.successions({ params: { co }, query: {} }),
    retry: false,
  });
  const answer = useMutation({
    mutationFn: (v: { id: string; answer: "accept" | "decline" }) =>
      api.client.memoryPeople.successionAnswer({
        params: { co, id: v.id },
        body: { answer: v.answer },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
    onError: (e) =>
      toast.add({
        title: "Could not answer",
        description: problemText(e, "Try again."),
        type: "error",
      }),
  });
  const open = (q.data?.items ?? []).filter((s) => s.can_answer);
  if (!open.length) return null;
  return (
    <Card
      slot="succession-cards"
      title="Take over this seat's memory"
      info="What the seat's earlier holder knew and decided can pass to you. Their cover over it ends when you say yes."
    >
      <ul className="mt-1 space-y-2">
        {open.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-2 text-[13px]">
            <span className="font-medium">{s.seat_title}</span>
            {s.from_name ? <span className="text-secondary">from {s.from_name}</span> : null}
            <span className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ id: s.id, answer: "accept" })}
              >
                Take over
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ id: s.id, answer: "decline" })}
              >
                Not me
              </Button>
            </span>
          </li>
        ))}
      </ul>
      <WaitingNote label={q.data?.doors.label} />
    </Card>
  );
}

function OnboardingCard({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const qc = useQueryClient();
  const key = peopleKey(co, "onboarding");
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.client.memoryPeople.onboarding({ params: { co } }),
    retry: false,
  });
  const act = useMutation({
    mutationFn: (start: boolean) =>
      start
        ? api.client.memoryPeople.onboardingStart({ params: { co } })
        : api.client.memoryPeople.onboardingDismiss({ params: { co } }),
    onSuccess: (next) => qc.setQueryData(key, next),
    onError: (e) =>
      toast.add({
        title: "Could not start",
        description: problemText(e, "Try again."),
        type: "error",
      }),
  });
  const v = q.data;
  if (!v || (v.state !== "offered" && v.state !== "asking")) return null;
  return (
    <Card
      slot="onboarding-card"
      title="Get set up with your Chief of Staff"
      info="Your Chief of Staff asks a few questions in chat, one at a time, so the team's memory starts with what matters. Reply there; say skip to pass on one."
    >
      {v.state === "asking" ? (
        <p className="mt-1 text-[13px]">
          {v.progress.done} of {v.progress.total} answered. Reply in your chat with the Chief of
          Staff.
        </p>
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <Button size="sm" disabled={act.isPending} onClick={() => act.mutate(true)}>
            Start
          </Button>
          {v.can_dismiss ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={act.isPending}
              onClick={() => act.mutate(false)}
            >
              Not now
            </Button>
          ) : null}
          {v.note ? <span className="text-xs text-muted-foreground">{v.note}</span> : null}
        </div>
      )}
    </Card>
  );
}

/** A space's (or the company's) charter, in a modal: what it is for and how it works. */
export function CharterDialog({
  api,
  co,
  kind,
  id,
  name,
  open,
  onClose,
}: {
  api: Api;
  co: string;
  kind: "company_bucket" | "space";
  id: string;
  name: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const key = peopleKey(co, "charter", kind, id);
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.client.memoryPeople.charter({ params: { co, kind, id } }),
    enabled: open,
    retry: false,
  });
  const [text, setText] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (t: string) =>
      api.client.memoryPeople.charterSet({ params: { co, kind, id }, body: { text: t } }),
    onSuccess: (next) => {
      qc.setQueryData(key, next);
      setText(null);
      onClose();
    },
    onError: (e) =>
      toast.add({
        title: "Could not save",
        description: problemText(e, "Try again."),
        type: "error",
      }),
  });
  const fromVision = useMutation({
    mutationFn: () => api.client.memoryPeople.charterFromVision({ params: { co } }),
    onSuccess: (next) => {
      qc.setQueryData(key, next);
      setText(null);
    },
  });
  const v = q.data;
  const shown = text ?? (v?.text || v?.suggested || "");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-dialog="charter" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Charter: {name}
            <InfoTip label="About the charter">
              A few lines on what this is for and how it works. Memory uses it to decide what
              belongs here.
            </InfoTip>
          </DialogTitle>
          <DialogDescription className="sr-only">Write the charter</DialogDescription>
        </DialogHeader>
        <Textarea
          aria-label="Charter"
          rows={5}
          maxLength={2000}
          disabled={!v?.can_edit}
          value={shown}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="flex items-center gap-2">
          {kind === "company_bucket" && v?.can_edit ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={fromVision.isPending}
              onClick={() => fromVision.mutate()}
            >
              Use the Vision
            </Button>
          ) : null}
          <WaitingNote
            label={v?.state === "waiting" || v?.state === "none" ? v?.doors.label : null}
          />
          <span className="ml-auto flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
            {v?.can_edit ? (
              <Button
                size="sm"
                disabled={save.isPending || !shown.trim()}
                onClick={() => save.mutate(shown.trim())}
              >
                Save
              </Button>
            ) : null}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Setup › Recall: the seats as roles in memory, and what still waits for the memory service. */
export function RolesPanel({ api, co }: { api: Api; co: string }) {
  const q = useQuery({
    queryKey: peopleKey(co, "roles"),
    queryFn: () => api.client.memoryPeople.roles({ params: { co } }),
    retry: false,
  });
  if (!q.data) return null;
  return (
    <section data-slot="memory-roles" className="mt-4">
      <h2 className="flex items-center gap-1 text-sm font-semibold">
        Seats in memory
        <InfoTip label="About seats in memory">
          Each seat is a role in memory. When a seat changes hands its memory goes to the next
          holder, who says yes first.
        </InfoTip>
      </h2>
      <ul className="mt-1 divide-y divide-subtle text-[13px]">
        {q.data.items.map((r) => (
          <li key={r.seat_id} className="flex items-center gap-2 py-1.5">
            <span className="font-medium">{r.title}</span>
            <span className="ml-auto text-xs text-muted-foreground">
              {r.state === "pending" ? "Waiting" : r.state}
            </span>
          </li>
        ))}
      </ul>
      {q.data.waiting > 0 ? <WaitingNote label={q.data.doors.label} /> : null}
    </section>
  );
}
